import type { Node, NodeAnnouncement, NodeConnections } from '../types.ts';
import type {
    ServerConfigurer,
    ServerConfigurerHandlerSpec,
    ServerHttpRequest
} from '@interopio/gateway-server/web/server';
import type {AuthorizationRule} from '@interopio/gateway-server/auth/api';

function buildCanonicalBaseURL(request: ServerHttpRequest): URL {
    const requestURL = request.URL;
    const protocol = requestURL.protocol === 'https:' ? 'wss://' : 'ws://';
    return new URL(protocol + requestURL.host);
}

function toCanonicalEndpoint(endpoint: string, request: ServerHttpRequest): string {

    const canonicalBaseURL = buildCanonicalBaseURL(request);

    const endpointURL = new URL(endpoint, canonicalBaseURL);
    if (endpointURL.protocol == canonicalBaseURL.protocol && endpointURL.host === canonicalBaseURL.host) {
        return endpointURL.pathname + endpointURL.search + endpointURL.hash;
    }
    else {
        return endpointURL.href;
    }
}

function fromCanonicalEndpoint(endpoint: string, request: ServerHttpRequest): string {
    const canonicalBaseURL = buildCanonicalBaseURL(request);
    const endpointURL = new URL(endpoint, canonicalBaseURL);
    return endpointURL.href;
}

function fromRequestNode(node: NodeAnnouncement, request: ServerHttpRequest): NodeAnnouncement {
    const endpoint = toCanonicalEndpoint(node.endpoint ?? `/cluster?node=${node.node}`, request);
    if (endpoint !== node.endpoint) {
        return { ...node, endpoint };
    }
    return node;
}
function toRequestNode(node: Node, request: ServerHttpRequest): Node {
    const endpoint = fromCanonicalEndpoint(node.endpoint, request);
    if (endpoint !== node.endpoint) {
        return { ...node, endpoint };
    }
    return node;
}

function routes(config: { connections: NodeConnections, authorize?: AuthorizationRule }, {handle}: ServerConfigurer) {
    const {connections, authorize} = config;
    handle(
        {
            request: {method: 'POST', path: '/api/nodes'}, options: {cors: true, authorize},
            handler: async ({request, response}) => {
                const json = await request.json();
                if (!Array.isArray(json)) {
                    response.setRawStatusCode(400 /*Bad Request*/);
                    await response.end();
                }
                else {
                    const nodes = (json as NodeAnnouncement[]).map((node) => {
                        return fromRequestNode(node, request);
                    });
                    const result = connections.announce(nodes).map((connection) => {
                            const connect = connection.connect?.map((node) => {
                                return toRequestNode(node, request);
                            });
                            return { ...connection, connect: connect }
                        });
                    const buffer = Buffer.from(JSON.stringify(result), 'utf8');
                    response.setRawStatusCode(200 /*OK*/);
                    response.headers.set('Content-Type', 'application/json; charset=utf8');
                    await response.body(buffer);
                }
            }
        },
        {
            request: {method: 'GET', path: /^\/api\/nodes\/(?<nodeId>.+)$/}, options: {cors: true, authorize},
            handler: async ({request, response}, {nodeId}) => {
                const result = connections.find(nodeId)?.map((node) => toRequestNode(node, request));
                if (result !== undefined) {
                    const buffer = Buffer.from(JSON.stringify(result), 'utf8');
                    response.setRawStatusCode(200 /* OK */);
                    response.headers.set('Content-Type', 'application/json; charset=utf8');
                    await response.body(buffer);
                } else {
                    response.setRawStatusCode(404 /* Not Found */);
                    await response.end();
                }
            },
        } as ServerConfigurerHandlerSpec<'nodeId'>,
        {
            request: {method: 'DELETE', path: /^\/api\/nodes\/(?<nodeId>.*)$/}, options: {cors: true, authorize},
            handler: async ({response}, {nodeId}) => {

                const removed = connections.remove(nodeId);
                if (removed) {
                    response.setRawStatusCode(200 /* OK */);
                } else {
                    response.setRawStatusCode(404 /* Not Found */);
                }
                await response.end();
            }
        } as ServerConfigurerHandlerSpec<'nodeId'>,
    );
}

export default routes;
